import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { passwordSchema } from "validation/schemas/password";

import Header from "components/Header";
import TextBox from "components/TextBox";
import { AppRoutes } from "pages/constants/routes";

import styles from "./ResetPassword.module.scss";
import AuthService from "api/Auth/AuthService";
import FinishDialog from "components/FinishDialog";

const ResetPassword = () => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>();
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    if (token !== null) {
      return;
    }

    navigate(AppRoutes.landing);
  }, [navigate, token]);

  const onChangeWrapper =
    (
      setValue: (newValue: string) => void,
      setError: (newValue?: string) => void,
    ) =>
    (newValue: string) => {
      setValue(newValue);
      setError();
    };

  const onPasswordSubmit = async () => {
    if (token === null) return;

    try {
      await passwordSchema.parseAsync({ password, confirmPassword });
      setIsSubmitting(true);
      await AuthService.resetPassword(token, password);
      setIsSuccessDialogOpen(true);
    } catch (err) {
      if (err instanceof Error) {
        const zodError = JSON.parse(err.message);
        zodError.forEach((error: any) => {
          if (error.path[0] === "confirmPassword") {
            setConfirmPasswordError(error.message);
          } else if (error.path[0] === "password") {
            setPasswordError(error.message);
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background} />
      <Header hideSignIn />
      <div className={styles.content}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>Reset Password</div>
            <div className={styles.subtitle}>Please provide a new password</div>
          </div>
          <div className={styles.inputContainer}>
            <TextBox
              lean
              password
              error={passwordError !== undefined}
              placeholder="Password"
              value={password}
              onChange={onChangeWrapper(setPassword, setPasswordError)}
            />
            {passwordError && (
              <div className={styles.fieldError}>{passwordError}</div>
            )}
            <TextBox
              lean
              password
              error={confirmPasswordError !== undefined}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={onChangeWrapper(
                setConfirmPassword,
                setConfirmPasswordError,
              )}
              onSubmit={isSubmitting ? undefined : onPasswordSubmit}
            />
            {confirmPasswordError && (
              <div className={styles.fieldError}>{confirmPasswordError}</div>
            )}
          </div>
          <div className={styles.signInContainer}>
            <span className={styles.text}>Return to</span>
            <Link to={AppRoutes.signin}>sign in</Link>
            <span className={styles.text}>•</span>
            <Link to={AppRoutes.signup}>sign up</Link>
          </div>
        </div>
      </div>

      {/* Success Dialog for successful ticket creation */}
      <FinishDialog
        isOpen={isSuccessDialogOpen}
        setIsOpen={setIsSuccessDialogOpen}
        title="Success!"
        message="Your new password has been registered, please proceed to signing in again."
        onConfirm={() => navigate(AppRoutes.signin)}
      />
    </div>
  );
};

export default ResetPassword;
